import React, { createContext, useContext, useMemo, useCallback, useEffect, useState } from 'react';

type Language = 'en' | 'zh';

type Translations = Record<string, Record<Language, string>>;

const translations: Translations = {
  // Header
  'nav.home': { en: 'Home', zh: '首页' },
  'nav.products': { en: 'Products', zh: '产品方案' },
  'nav.cases': { en: 'Cases', zh: '应用案例' },
  'nav.partners': { en: 'Partners', zh: '合作伙伴' },
  'nav.apps': { en: 'App Factory', zh: 'App工厂' },
  'nav.contact': { en: 'Contact', zh: '联系我们' },
  'brand.text': { en: 'MiaoZhi — Future Is Here', zh: '淼智--未来以来' },
  'toggle.zh': { en: '中文', zh: '中文' },
  'toggle.en': { en: 'EN', zh: 'EN' },

  // Hero
  'hero.title': { en: 'One-stop AI Agent Solutions', zh: 'AI agent一站式解决方案' },
  'hero.subtitle': { en: 'Make AI the core driver of productivity and innovation across industries', zh: '让人工智能真正成为各行业生产力提升和创新驱动的核心动力' },
  'hero.cta.primary': { en: 'Free Consultation & Trial', zh: '免费咨询和试用' },
  'hero.cta.secondary': { en: 'Claim Free Trial Quota', zh: '立即抢占免费试用名额' },
  'hero.card.title': { en: 'One-stop AI Agent Solutions', zh: 'AI agent一站式解决方案' },
  'hero.card.desc': { en: 'Accelerate AI adoption in healthcare, legal, HR, consulting and more with tailored AI Agents and industry solutions', zh: '通过定制化AI Agent应用和行业智能解决方案，加速人工智能在医疗、法律、人力、咨询等关键领域的应用落地' },
  'hero.stat.coreIndustries': { en: 'Core industries', zh: '核心行业' },
  'hero.stat.customized': { en: 'Customized plans', zh: '定制化方案' },
  'hero.stat.fast': { en: 'Fast', zh: '快速' },
  'hero.stat.dev': { en: 'Responsive dev', zh: '响应开发' },
  'hero.stat.ai': { en: 'AI', zh: 'AI' },
  'hero.stat.landing': { en: 'Adoption', zh: '应用落地' },

  // Products
  'products.section.title': { en: 'One-stop AI Agent Solutions', zh: 'AI agent一站式解决方案' },
  'products.section.subtitle': { en: 'Rapidly build and explore AI applications on top of existing LLMs', zh: '基于市场已有大模型快速响应AI应用的开发、探索' },
  'products.more': { en: 'Learn more', zh: '了解更多' },

  // Cases
  'cases.section.title': { en: 'AI Application Cases', zh: 'AI应用案例' },
  'cases.section.subtitle': { en: 'Single-agent deployments solving real business problems', zh: '单智能体实施落地解决单个实际业务问题' },
  'cases.view.images': { en: 'View case images', zh: '查看案例图片' },
  'cases.view.detail': { en: 'View details', zh: '查看详情' },
  'cases.image.failed': { en: 'Image failed to load', zh: '图片加载失败' },
  'cases.thumb.alt': { en: 'Thumbnail', zh: '缩略图' },

  // CTA section
  'cta.title': { en: 'One-stop AI Agent solutions to make your digital transformation more efficient and competitive!', zh: 'AI agent一站式解决方案，让您的企业智能化转型更高效、竞争力更强！' },
  'cta.desc': { en: 'Book a free tailored AI plan consultation! First 100 users get 1:1 expert support and free AI Agent trial. We welcome partners to grow together.', zh: '免费预约AI应用方案定制咨询！前100名咨询用户，可额外获得技术专家一对一专业解答及AI Agent免费试用！我们正在寻找合作伙伴共同发展。' },
  'cta.primary': { en: 'Claim Free Trial Quota', zh: '立即抢占免费试用名额' },
  'cta.secondary': { en: 'Free Consultation & Trial', zh: '免费咨询和试用' },
  'features.section.title': { en: 'One-stop AI Agent Solutions', zh: 'AI agent一站式解决方案' },
  'features.section.subtitle': { en: 'Rapidly build and explore AI applications on top of existing LLMs', zh: '基于市场已有大模型快速响应AI应用的开发、探索' },
  'features.cta.title': { en: 'Start your AI transformation', zh: '开启企业AI智能化转型' },
  'features.cta.desc': { en: 'Book a free tailored AI plan consultation! First 100 users also get a free AI Agent trial.', zh: '免费预约AI应用方案定制咨询！前100名咨询用户，可额外获得技术专家一对一专业解答及AI Agent免费试用！' },
  'features.cta.primary': { en: 'Claim Free Trial Quota', zh: '立即抢占免费试用名额' },

  // Products items
  'product.medical.title': { en: 'Healthcare AI Agent', zh: '医疗AI Agent' },
  'product.medical.desc': { en: 'AI agents tailored for healthcare to enable intelligent workflows and automation', zh: '专为医疗行业定制的AI智能体，实现医疗流程智能化和自动化管理' },
  'product.medical.f1': { en: 'Intelligent diagnosis assist', zh: '智能诊断辅助' },
  'product.medical.f2': { en: 'Healthcare workflow automation', zh: '医疗流程自动化' },
  'product.medical.f3': { en: 'Smart patient management', zh: '患者管理智能化' },
  'product.medical.f4': { en: 'Medical data analytics', zh: '医疗数据分析' },

  'product.legal.title': { en: 'Legal AI Agent', zh: '法律AI Agent' },
  'product.legal.desc': { en: 'Legal assistant with knowledge graph, case search, document generation and review', zh: '法律行业专用AI助手，集成法律知识图谱、案例检索、文书生成等核心功能' },
  'product.legal.f1': { en: 'Legal knowledge graph', zh: '法律知识图谱' },
  'product.legal.f2': { en: 'Case retrieval system', zh: '案例检索系统' },
  'product.legal.f3': { en: 'Document generation', zh: '文书生成工具' },
  'product.legal.f4': { en: 'Contract review AI', zh: '合同审查AI' },

  'product.hr.title': { en: 'HR AI Agent', zh: '人力资源AI Agent' },
  'product.hr.desc': { en: 'Smart HR solutions to improve efficiency and tech adoption', zh: '人力资源管理智能化解决方案，提高企业科技占比和运营效率' },
  'product.hr.f1': { en: 'Intelligent recruiting', zh: '智能招聘系统' },
  'product.hr.f2': { en: 'Employee training AI', zh: '员工培训AI' },
  'product.hr.f3': { en: 'Performance analytics', zh: '绩效分析工具' },
  'product.hr.f4': { en: 'HR process automation', zh: 'HR流程自动化' },

  'product.consult.title': { en: 'Consulting AI Agent', zh: '咨询AI Agent' },
  'product.consult.desc': { en: 'Upgrade consulting services with accurate analytics and solutions', zh: '咨询服务智能化升级，提供更精准的数据分析和解决方案' },
  'product.consult.f1': { en: 'Consulting assistant', zh: '智能咨询助手' },
  'product.consult.f2': { en: 'Data analytics services', zh: '数据分析服务' },
  'product.consult.f3': { en: 'Solution generation', zh: '方案生成系统' },
  'product.consult.f4': { en: 'Customer management AI', zh: '客户管理AI' },

  'product.custom.title': { en: 'Custom AI Agent', zh: '定制化AI Agent' },
  'product.custom.desc': { en: 'Tailored AI agents rapidly built for your specific business needs', zh: '基于企业特定需求定制开发的AI智能体，快速响应业务需求' },
  'product.custom.f1': { en: 'Personalized AI development', zh: '个性化AI开发' },
  'product.custom.f2': { en: 'Rapid deployment', zh: '快速部署实施' },
  'product.custom.f3': { en: 'Continuous optimization', zh: '持续优化升级' },
  'product.custom.f4': { en: 'Technical team support', zh: '技术团队支持' },

  'product.transform.title': { en: 'Enterprise Intelligent Transformation', zh: '企业智能化转型' },
  'product.transform.desc': { en: 'Help enterprises achieve intelligent processes and management to boost competitiveness', zh: '助力企业实现智能化流程及管理，提高科技占比和竞争力' },
  'product.transform.f1': { en: 'Intelligent process design', zh: '智能化流程设计' },
  'product.transform.f2': { en: 'Efficiency improvement', zh: '管理效率提升' },
  'product.transform.f3': { en: 'Tech ratio optimization', zh: '科技占比优化' },
  'product.transform.f4': { en: 'Digital transformation', zh: '数字化转型' },

  'product.petmoji.title': { en: 'PetMoji Create', zh: 'PetMoji 宠物表情' },
  'product.petmoji.desc': { en: 'AI-powered app that transforms pet photos into high-quality, expressive emojis and animated stickers.', zh: '基于AI将宠物照片转化为高质量、富有表现力的表情包和动态贴纸。' },
  'product.petmoji.f1': { en: 'Instant pet emojis', zh: '一键生成宠物表情' },
  'product.petmoji.f2': { en: '30+ Unique Styles', zh: '30+种独特风格' },
  'product.petmoji.f3': { en: 'Animated GIFs', zh: '动态GIF表情' },
  'product.petmoji.f4': { en: 'Reaction Packs', zh: '专属情绪表情包' },

  // Features items (section grid)
  'feature.medical.title': { en: 'Healthcare AI Applications', zh: '医疗行业AI应用' },
  'feature.medical.desc': { en: 'Apply AI workflows in healthcare to enable intelligent operations', zh: '将AI智能流程应用到医疗企业中，实现企业智能化流程及管理' },
  'feature.medical.l1': { en: 'Intelligent diagnosis assistance', zh: '智能诊断辅助系统' },
  'feature.medical.l2': { en: 'Healthcare workflow automation', zh: '医疗流程自动化' },
  'feature.medical.l3': { en: 'Smart patient management', zh: '患者管理智能化' },
  'feature.medical.l4': { en: 'Medical data analytics', zh: '医疗数据分析' },

  'feature.legal.title': { en: 'Legal AI Applications', zh: '法律行业AI应用' },
  'feature.legal.desc': { en: 'AI assistants for legal workflows to boost efficiency', zh: '专为法律行业打造的智能AI助手系统，提升工作效率' },
  'feature.legal.l1': { en: 'Legal knowledge graph', zh: '法律知识图谱' },
  'feature.legal.l2': { en: 'Case retrieval system', zh: '案例检索系统' },
  'feature.legal.l3': { en: 'Document generation', zh: '文书生成工具' },
  'feature.legal.l4': { en: 'Contract review AI', zh: '合同审查AI' },

  'feature.hr.title': { en: 'HR AI Applications', zh: '人力资源AI应用' },
  'feature.hr.desc': { en: 'Intelligent HR solutions to raise tech adoption', zh: '人力资源管理的智能化解决方案，提高企业科技占比' },
  'feature.hr.l1': { en: 'Intelligent recruiting', zh: '智能招聘系统' },
  'feature.hr.l2': { en: 'Employee training AI', zh: '员工培训AI' },
  'feature.hr.l3': { en: 'Performance analytics', zh: '绩效分析工具' },
  'feature.hr.l4': { en: 'HR process automation', zh: 'HR流程自动化' },

  'feature.consult.title': { en: 'Consulting AI Applications', zh: '咨询行业AI应用' },
  'feature.consult.desc': { en: 'Upgrade consulting with accurate solutions', zh: '咨询服务的智能化升级，提供更精准的解决方案' },
  'feature.consult.l1': { en: 'Consulting assistant', zh: '智能咨询助手' },
  'feature.consult.l2': { en: 'Data analytics services', zh: '数据分析服务' },
  'feature.consult.l3': { en: 'Solution generation', zh: '方案生成系统' },
  'feature.consult.l4': { en: 'Customer management AI', zh: '客户管理AI' },

  'feature.custom.title': { en: 'Custom AI Agent', zh: '定制化AI Agent' },
  'feature.custom.desc': { en: 'Tailored AI agents for your needs', zh: '基于企业需求定制开发AI智能体，快速响应业务需求' },
  'feature.custom.l1': { en: 'Personalized AI development', zh: '个性化AI开发' },
  'feature.custom.l2': { en: 'Rapid deployment', zh: '快速部署实施' },
  'feature.custom.l3': { en: 'Continuous optimization', zh: '持续优化升级' },
  'feature.custom.l4': { en: 'Technical team support', zh: '技术团队支持' },

  'feature.transform.title': { en: 'Enterprise Intelligent Transformation', zh: '企业智能化转型' },
  'feature.transform.desc': { en: 'Enable intelligent processes and management', zh: '助力企业实现智能化流程及管理，提高科技占比' },
  'feature.transform.l1': { en: 'Intelligent process design', zh: '智能化流程设计' },
  'feature.transform.l2': { en: 'Efficiency improvement', zh: '管理效率提升' },
  'feature.transform.l3': { en: 'Tech ratio optimization', zh: '科技占比优化' },
  'feature.transform.l4': { en: 'Digital transformation', zh: '数字化转型' },

  // Cases items
  'case.auto.title': { en: 'Auto 4S Store AI Sales Integration', zh: '汽车4S店AI销售方案融合' },
  'case.auto.desc': { en: 'AI-driven sales with customer profiling, recommendations, and automated workflows to boost conversions and satisfaction.', zh: '基于AI技术的汽车4S店智能销售解决方案，通过客户画像分析、智能推荐系统、销售流程自动化等技术，实现精准营销和个性化服务。涵盖客户管理、销售跟进、售后服务全流程，显著提升销售转化率和客户满意度。' },
  'case.auto.stat1': { en: 'Sales conversion increase', zh: '销售转化率提升' },
  'case.auto.stat2': { en: 'Customer satisfaction', zh: '客户满意度' },

  'case.legal.title': { en: 'Legal AI Assistant', zh: '律师AI小助手' },
  'case.legal.desc': { en: 'Legal research assistant with case search, document generation, and contract review to improve efficiency and quality.', zh: '专为法律行业打造的智能AI助手系统，集成法律知识图谱、案例检索、文书生成、合同审查等核心功能。通过自然语言处理和机器学习技术，为律师提供高效的法律研究支持，大幅提升工作效率和案件处理质量。' },
  'case.legal.stat1': { en: 'Efficiency improvement', zh: '工作效率提升' },
  'case.legal.stat2': { en: 'Search accuracy', zh: '法律检索准确率' },

  'case.insurance.title': { en: 'Commercial Health Insurance AI Marketing', zh: '商业医疗保险AI一体化营销' },
  'case.insurance.desc': { en: 'AI-powered marketing with profiling, risk evaluation, recommendations, and automation to boost sales efficiency.', zh: '针对商业医疗保险行业的AI驱动营销解决方案，整合客户画像分析、风险评估、产品推荐、销售自动化等核心模块。通过大数据分析和智能算法，实现精准营销、风险控制和客户生命周期管理，显著提升保险销售效率和客户体验。' },
  'case.insurance.stat1': { en: 'Marketing conversion increase', zh: '营销转化率提升' },
  'case.insurance.stat2': { en: 'Acquisition cost reduction', zh: '获客成本降低' },

  // CTA features grid
  'cta.feature.fast.title': { en: 'Rapid response', zh: '快速响应' },
  'cta.feature.fast.desc': { en: 'Quickly build AI apps on existing LLMs', zh: '基于市场已有大模型快速开发AI应用' },
  'cta.feature.team.title': { en: 'Team expansion', zh: '团队扩展' },
  'cta.feature.team.desc': { en: 'Scale engineering for rapid delivery and deployment', zh: '需要扩展团队进行快速响应开发和部署' },
  'cta.feature.custom.title': { en: 'Customized solutions', zh: '定制化方案' },
  'cta.feature.custom.desc': { en: 'Tailored AI Agents to solve real business problems', zh: '通过定制化AI Agent应用解决企业实际问题' },
  'cta.feature.brand.title': { en: 'Brand building', zh: '品牌建设' },
  'cta.feature.brand.desc': { en: 'Build proprietary products and go to market', zh: '打造公司的自主品牌产品，推向市场' },

  // Footer about links
  'about.vision': { en: 'Vision & Mission', zh: '公司愿景与使命' },
  'about.team': { en: 'Core Team', zh: '核心团队介绍' },
  'about.awards': { en: 'Awards', zh: '荣誉资质' },

  // Footer
  'footer.brand.name': { en: 'MiaoZhi AI', zh: 'MiaoZhi AI' },
  'footer.brand.desc': { en: 'MiaoZhi is an enterprise AI platform dedicated to making AI adoption easy with a full suite of tools and ecosystem services.', zh: 'MiaoZhi（淼智科技）是一个专注于企业级AI应用落地的综合性平台，致力于"让天下没有难做的AI"，通过提供完整的AI开发管理套件和生态服务，帮助企业实现AI技术的快速落地和商业化应用。' },
  'footer.section.eco': { en: 'MiaoZhi Ecosystem', zh: 'MiaoZhi价值生态' },
  'footer.section.products': { en: 'Products', zh: '产品服务' },
  'footer.section.industry': { en: 'Industry', zh: '行业应用' },
  'footer.section.about': { en: 'About Us', zh: '关于我们' },
  'footer.link.resources': { en: 'Resources', zh: '资源对接' },
  'footer.link.ecosystem': { en: 'Ecosystem', zh: 'MiaoZhi生态' },
  'footer.link.partnership': { en: 'Partnership', zh: '合作生态' },
  'footer.link.case': { en: 'Case Studies', zh: '合作生态案例' },
  'footer.link.suite': { en: 'Dev Suite', zh: 'MiaoZhi开发管理套件' },
  'footer.link.market': { en: 'Marketplace', zh: 'MiaoZhi应用市场' },
  'footer.link.platform': { en: 'AI App Platform', zh: 'AI应用管理平台' },
  'footer.link.franchise': { en: 'Franchise', zh: '区域加盟' },
  'footer.link.edu': { en: 'AI + Education', zh: 'AI+教育' },
  'footer.link.health': { en: 'AI + Healthcare', zh: 'AI+医疗' },
  'footer.link.ecom': { en: 'AI + E-commerce', zh: 'AI+电商' },
  'footer.link.finance': { en: 'AI + Finance', zh: 'AI+金融' },
  'footer.copyright': { en: '© 2025 Beijing MiaoZhi Technology Center Co., Ltd.', zh: '© 2025 北京淼智科技中心有限公司' },
  'footer.links.friend': { en: 'Friend Links', zh: '友情链接' },
  'footer.links.souwai': { en: 'Souwai Links', zh: '搜外友链' },
  'footer.links.privacy': { en: 'Privacy Policy', zh: '隐私政策' },
  'footer.links.terms': { en: 'Terms of Service', zh: '服务条款' },

  // App Factory
  'apps.factory.title': { en: 'App Factory', zh: 'App工厂' },
  'apps.factory.subtitle': {
    en: 'Browse all projects and open each app home page for downloads and legal links.',
    zh: '集中展示所有项目。点击进入对应 App 首页，获取下载入口与 EULA/隐私政策链接。',
  },
  'apps.factory.open': { en: 'Open', zh: '打开' },

  'apps.detail.notFound': { en: 'App not found', zh: '未找到对应的 App' },
  'apps.detail.backToFactory': { en: 'Back to App Factory', zh: '返回 App工厂' },
  'apps.detail.downloadTitle': { en: 'Download', zh: '下载地址' },
  'apps.detail.legalTitle': { en: 'Legal', zh: '条款与隐私' },
  'apps.detail.extraLinksTitle': { en: 'More links', zh: '更多入口' },
  'apps.detail.manageSubscription.ios': { en: 'Manage subscription (iOS)', zh: '管理订阅（iOS）' },
  'apps.detail.manageSubscription.android': { en: 'Manage subscription (Android)', zh: '管理订阅（Android）' },
  'apps.detail.screenshotsTitle': { en: 'Screenshots', zh: '应用截图' },
  'apps.detail.screenshots.open': { en: 'Open screenshot preview', zh: '打开截图预览' },

  // Legal
  'legal.terms': { en: 'Terms of Use (EULA)', zh: '使用条款（EULA）' },
  'legal.privacy': { en: 'Privacy Policy', zh: '隐私政策' },
  'legal.appliesTo': { en: 'Applies to', zh: '适用于' },
  'legal.lastUpdated': { en: 'Last updated', zh: '最后更新' },
  'legal.backHome': { en: 'Back Home', zh: '返回首页' },

  'legal.contact.title': { en: 'Contact', zh: '联系我们' },
  'legal.contact.desc': { en: 'If you have questions, email:', zh: '如有任何问题，请联系邮箱：' },

  'legal.privacy.intro': {
    en: 'This Privacy Policy explains how we collect, use, and protect your information when you use our services.',
    zh: '本《隐私政策》说明我们在你使用相关服务/应用时，如何收集、使用与保护你的信息。',
  },
  'legal.privacy.collect.title': { en: 'Information we collect', zh: '我们收集的信息' },
  'legal.privacy.collect.item1': { en: 'Basic device and app diagnostics (crash logs, performance).', zh: '基础设备与应用诊断信息（如崩溃日志、性能数据）。' },
  'legal.privacy.collect.item2': { en: 'Account / subscription related identifiers when applicable.', zh: '在订阅/账号相关场景下的必要标识信息。' },
  'legal.privacy.collect.item3': { en: 'User-provided content you choose to submit to the app.', zh: '你主动提供给应用的内容（例如输入/上传的内容）。' },
  'legal.privacy.use.title': { en: 'How we use information', zh: '我们如何使用信息' },
  'legal.privacy.use.item1': { en: 'Provide and maintain features you request.', zh: '提供并维护你所使用的功能。' },
  'legal.privacy.use.item2': { en: 'Improve stability, security, and user experience.', zh: '提升稳定性、安全性与用户体验。' },
  'legal.privacy.use.item3': { en: 'Comply with legal and regulatory requirements.', zh: '遵循法律法规与监管要求。' },
  'legal.privacy.share.title': { en: 'Sharing', zh: '信息共享' },
  'legal.privacy.share.desc': {
    en: 'We do not sell personal information. We may share limited data with service providers strictly for providing the service, and only as necessary.',
    zh: '我们不会出售个人信息。仅在提供服务所必需的情况下，可能与服务提供商共享有限数据，并采取必要的保护措施。',
  },

  'legal.terms.intro': {
    en: 'These Terms of Use (EULA) govern your access to and use of our apps and services.',
    zh: '本《使用条款（EULA）》适用于你访问与使用我们的应用与相关服务。',
  },
  'legal.terms.license.title': { en: 'License', zh: '许可与授权' },
  'legal.terms.license.item1': { en: 'We grant you a personal, non-exclusive, non-transferable license to use the app.', zh: '我们授予你个人、非独占、不可转让的应用使用许可。' },
  'legal.terms.license.item2': { en: 'You may not reverse engineer, resell, or misuse the app.', zh: '你不得对应用进行逆向工程、转售或以其他方式滥用。' },
  'legal.terms.user.title': { en: 'User obligations', zh: '用户义务' },
  'legal.terms.user.item1': { en: 'Use the app in compliance with applicable laws.', zh: '你应遵守适用法律法规使用应用。' },
  'legal.terms.user.item2': { en: 'Do not attempt to disrupt, damage, or gain unauthorized access.', zh: '不得破坏、干扰系统运行或进行未授权访问。' },
  'legal.terms.user.item3': { en: 'You are responsible for the content you submit.', zh: '你对自己提交/生成/上传的内容承担相应责任。' },
  'legal.terms.disclaimer.title': { en: 'Disclaimer', zh: '免责声明' },
  'legal.terms.disclaimer.desc': {
    en: 'Services are provided “as is” to the maximum extent permitted by law. We do not guarantee uninterrupted or error-free operation.',
    zh: '在法律允许的最大范围内，服务按“现状”提供，我们不保证服务不中断或完全无错误。',
  },

  // Partners section
  'partners.section.title': { en: 'Technology Partners', zh: '技术合作伙伴' },
  'partners.section.subtitle': { en: 'We collaborate with leading AI companies worldwide to deliver cutting-edge solutions for enterprises.', zh: '与全球领先的AI技术公司深度合作，为企业提供最先进的技术解决方案' },
  'partners.dify.desc': { en: "AI App development platform that makes LLMs accessible to everyone", zh: "AI应用开发平台，让AI大模型从'巨头专属'变为'触手可及'" },
  'partners.dify.f1': { en: 'Visual AI app development', zh: '可视化AI应用开发' },
  'partners.dify.f2': { en: 'Multi-model support', zh: '多模型支持' },
  'partners.dify.f3': { en: 'Enterprise-grade security', zh: '企业级安全' },
  'partners.dify.f4': { en: 'On-premise deployment', zh: '私有化部署' },
  'partners.fastgpt.desc': { en: 'Open-source AI knowledge base platform for building proprietary KBs', zh: '开源AI知识库构建平台，打造专属AI知识库' },
  'partners.fastgpt.f1': { en: 'Knowledge base construction', zh: '知识库构建' },
  'partners.fastgpt.f2': { en: 'RAG integration', zh: 'RAG技术集成' },
  'partners.fastgpt.f3': { en: 'Multiple data sources', zh: '多数据源支持' },
  'partners.fastgpt.f4': { en: 'Intelligent Q&A', zh: '智能问答' },
  'partners.ragflow.desc': { en: 'RAG solutions enabling unstructured document processing for enterprises', zh: 'RAG解决方案，让AI智能体非结构化文档处理能力在企业落地' },
  'partners.ragflow.f1': { en: 'Intelligent document processing', zh: '文档智能处理' },
  'partners.ragflow.f2': { en: 'RAG optimization', zh: 'RAG技术优化' },
  'partners.ragflow.f3': { en: 'Multi-format support', zh: '多格式支持' },
  'partners.ragflow.f4': { en: 'Enterprise deployment', zh: '企业级部署' },
};

type LanguageContextType = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Language>('en');

  useEffect(() => {
    const saved = (localStorage.getItem('lang') as Language | null);
    if (saved === 'zh' || saved === 'en') {
      setLangState(saved);
    } else {
      setLangState('en');
    }
  }, []);

  const setLang = useCallback((next: Language) => {
    setLangState(next);
    try { localStorage.setItem('lang', next); } catch {}
  }, []);

  const t = useCallback((key: string) => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] ?? entry.en ?? key;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}


